import { useCallback, useMemo, useState } from 'react';
import { createFileRoute, Outlet, redirect, useMatches, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PERMISSIONS } from '@siteflow/shared';
import { AppSidebar } from '@/components/app-sidebar';
import { CommandPalette, useCommandPaletteShortcut } from '@/components/command-palette';
import { ContentShell } from '@/components/content-shell';
import { TopBar, type Crumb } from '@/components/top-bar';
import { getAuthState, setLastOrgId } from '@/lib/auth-store';
import { findContentSiteId } from '@/lib/content-home';
import { ActiveOrgContext } from '@/lib/permissions';
import { CONTENT_ONLY, isContentPath } from '@/lib/ui-mode';
import { organisationDetailQuery } from '@/lib/api/organisations';
import { siteKeys } from '@/lib/api/sites';
import type { SiteDetail } from '@/lib/api/types';

/** Breadcrumb copy per child segment. */
const SEGMENT_LABELS: Record<string, string> = {
  sites: 'Sites',
  content: 'Content',
  media: 'Media',
  licence: 'Licence',
  billing: 'Billing',
  'audit-log': 'Audit log',
  team: 'Team',
  settings: 'Settings',
  pages: 'Pages',
  publish: 'Publish',
  environments: 'Environments',
  domains: 'Domains',
  'api-keys': 'API keys',
};

function labelFor(segment: string): string {
  return SEGMENT_LABELS[segment] ?? segment;
}

function OrgLayout() {
  const { orgId } = Route.useParams();
  const matches = useMatches();
  const queryClient = useQueryClient();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // `strict: false` because these params only exist under the site routes.
  const siteParams = useParams({ strict: false }) as { siteId?: string; pageId?: string };

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  useCommandPaletteShortcut(openPalette);

  const organisation = getAuthState().organisations.find((org) => org.id === orgId);

  const crumbs = useMemo<Crumb[]>(() => {
    const deepest = matches[matches.length - 1]?.pathname ?? '';
    const tail = deepest.replace(`/app/${orgId}`, '').replace(/^\/+|\/+$/g, '');
    const segments = tail ? tail.split('/') : [];

    const trail: Crumb[] = [
      { label: organisation?.name ?? 'Organisation', to: '/app/$orgId', params: { orgId } },
    ];

    if (segments.length === 0) {
      trail.push({ label: 'Overview' });
      return trail;
    }

    // Site routes read `sites / <site name> / <tab>` rather than showing ids.
    if (segments[0] === 'sites' && siteParams.siteId) {
      const site = queryClient.getQueryData<SiteDetail>(siteKeys.detail(orgId, siteParams.siteId));
      trail.push({ label: 'Sites', to: '/app/$orgId/sites', params: { orgId } });
      trail.push({
        label: site?.name ?? 'Site',
        to: '/app/$orgId/sites/$siteId',
        params: { orgId, siteId: siteParams.siteId },
      });

      const tab = segments[2];
      if (tab) trail.push({ label: labelFor(tab) });
      return trail;
    }

    trail.push({ label: labelFor(segments[0]!) });
    return trail;
  }, [matches, orgId, organisation?.name, queryClient, siteParams.siteId]);

  if (CONTENT_ONLY) {
    return (
      <ActiveOrgContext.Provider value={orgId}>
        <ContentShell orgId={orgId} />
      </ActiveOrgContext.Provider>
    );
  }

  return (
    <ActiveOrgContext.Provider value={orgId}>
      <div className="flex min-h-dvh bg-surface">
        <AppSidebar orgId={orgId} />
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar crumbs={crumbs} onOpenCommandPalette={openPalette} />
          <main className="min-w-0 flex-1 px-5 py-6 sm:px-8 sm:py-8">
            <Outlet />
          </main>
        </div>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} orgId={orgId} />
    </ActiveOrgContext.Provider>
  );
}

export const Route = createFileRoute('/app/$orgId')({
  /** Membership guard — the API is still the authority, this avoids a dead render. */
  beforeLoad: async ({ params, location, context }) => {
    const { organisations } = getAuthState();
    const member = organisations.some((org) => org.id === params.orgId);

    if (!member) {
      toast.error('You do not have access to that organisation.');
      throw redirect({ to: '/app' });
    }

    setLastOrgId(params.orgId);

    // Content-only builds show editors four screens and nothing else; anything
    // technical in the address bar goes to the site's pages instead.
    if (CONTENT_ONLY) {
      const tail = location.pathname.slice(`/app/${params.orgId}`.length) || '/';
      if (!isContentPath(tail)) {
        const siteId = await findContentSiteId(context.queryClient, params.orgId);
        if (siteId) {
          // People who can edit land on the live editor; someone who can only look
          // gets the list of pages, since there is nothing for them to change.
          const mayEdit = organisations
            .find((org) => org.id === params.orgId)
            ?.permissions.includes(PERMISSIONS.CONTENT_EDIT);
          throw redirect({
            to: mayEdit ? '/app/$orgId/sites/$siteId/edit' : '/app/$orgId/sites/$siteId/pages',
            params: { orgId: params.orgId, siteId },
          });
        }
        // No site to edit yet: the index shows a plain explanation.
        if (tail !== '/') throw redirect({ to: '/app/$orgId', params: { orgId: params.orgId } });
      }
    }

    const organisation = organisations.find((org) => org.id === params.orgId);
    return {
      permissions: organisation?.permissions ?? [],
      roleKeys: organisation?.roleKeys ?? [],
    };
  },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(organisationDetailQuery(params.orgId)),
  component: OrgLayout,
});
